import React from 'react';
import { Globe, MapPin, Building, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const MarketsSection = () => {
  const { t, isRTL } = useLanguage();

  const regions = Array.isArray(t('markets.regions')) ? t('markets.regions') : [];

  return (
    <section className="py-32 bg-slate-900 text-white relative overflow-hidden">
      {/* Premium Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="creative-grid absolute inset-0 opacity-20" />
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-purple-600/30 to-orange-500/30 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-500/30 to-purple-600/30 rounded-full blur-3xl animate-float"
          style={{ animationDelay: '-3s' }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-20">
          {/* Billion-Dollar Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full border border-purple-400/30 mb-8">
            <Globe className="w-4 h-4 text-purple-400 mr-2" />
            <span className="text-sm font-medium text-slate-300">
              {t('markets.badgeLabel')}
            </span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-light text-white mb-6 text-center">
            {t('markets.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('markets.highlight')}
            </span>
          </h2>

          <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed font-light mb-12 text-center">
            {t('markets.description')}
          </p>
        </div>

        {/* Regional Excellence Cards (Before Claim) */}
        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {regions.map((region, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-3xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/30 card-premium"
            >
              {/* Regional Monument */}
              <div className="relative h-80 overflow-hidden">
                <img
                  src={region.image}
                  alt={region.monument}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${region.gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-300`}
                />

                {/* Elite Badge */}
                <div className="absolute top-4 right-4">
                  <div
                    className={`inline-flex items-center px-3 py-1 bg-gradient-to-r ${region.gradient} rounded-full text-white text-xs font-medium backdrop-blur-sm`}
                  >
                    <Building className="w-3 h-3 mr-1" />
                    {region.monument.split(' ')[0]}
                  </div>
                </div>
              </div>

              {/* Elite Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  <div
                    className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${region.gradient} rounded-full text-white text-sm font-medium mb-4`}
                  >
                    <Sparkles className="w-3 h-3 mr-2" />
                    {region.specialty}
                  </div>

                  <h3 className="text-3xl font-light text-white mb-2">
                    {region.name}
                  </h3>

                  <p className="text-slate-300 text-sm mb-4 font-light">
                    {region.description}
                  </p>

                  <div className="space-y-2">
                    {region.countries.map((country, idx) => (
                      <div
                        key={idx}
                        className="flex items-center text-slate-300"
                      >
                        <MapPin className="w-4 h-4 mr-3 text-purple-400" />
                        <span className="font-medium">{country}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Premium Glow Effect */}
              <div
                className={`absolute inset-0 bg-gradient-to-t ${region.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-3xl`}
              />
            </div>
          ))}
        </div>

        {/* Billion-Dollar Connection Visualization */}
        <div className="text-center">
          <p className="text-lg text-slate-400 mb-8 font-light">
            {t('markets.connectionText')}
          </p>

          {/* Premium Connection Animation */}
          <div className="flex justify-center items-center space-x-8 max-w-2xl mx-auto mb-16">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center border-2 border-purple-400/50 relative">
              <span className="text-white font-bold text-lg">DE</span>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 animate-ping opacity-20" />
            </div>
            <div className="flex-1 h-1 bg-gradient-to-r from-purple-500 via-orange-400 to-purple-500 animate-pulse rounded-full" />
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center border-2 border-orange-400/50 relative">
              <span className="text-white font-bold text-lg">ME</span>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-orange-500 to-red-500 animate-ping opacity-20" />
            </div>
            <div className="flex-1 h-1 bg-gradient-to-r from-orange-500 via-emerald-400 to-orange-500 animate-pulse rounded-full" />
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-center border-2 border-emerald-400/50 relative">
              <span className="text-white font-bold text-lg">AF</span>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 animate-ping opacity-20" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
